import React, { useEffect, useRef, useState } from "react";
import { Modal, Button, Spinner } from "react-bootstrap";
import Loader from "../common/Loader.jsx";
import "./email-module.css";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { getMailTemplateApi, sendQuotationMailApi } from "./quotation-api";

import DOMPurify from "dompurify";
const MAX_ATTACHMENTS = 5;

const ComposeModal = ({ id, show, handleClose, succesError, templateId }) => {
  const [chips, setChips] = useState([]);
  const [bccChips, setBccChips] = useState([]);
  const [bccInputValue, setBccInputValue] = useState("");
  const [showBcc, setShowBcc] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [subjectValue, setSubjectValue] = useState("");
  const [descriptionValue, setDescriptionValue] = useState("");
  const [toValue, setToValue] = useState("");
  const [fromValue, setFromValue] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [attachmentError, setAttachmentError] = useState("");
  const [templateLoading, setTemplateLoading] = useState(false);
  const [sending, setSending] = useState(false);

  const attachmentInputRef = useRef(null);
  const emailEditorRef = useRef(null);

  const HtmlContent = ({ html }) => {
    return <div dangerouslySetInnerHTML={{ __html: html }} />;
  };
  const [contentValue, setContentValue] = useState("");
  const [contentViewValue, setContentViewValue] = useState("");
  const [editValue, setEditValue] = useState("");
  const addChip = () => {
    const value = inputValue.trim();

    if (value === "") return;

    setChips([...chips, value]);
    setInputValue("");
  };

  const removeChip = (index) => {
    setChips(chips.filter((_, i) => i !== index));
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === "," || e.key === " ") {
      e.preventDefault();
      addChip();
    }
  };

  const addBccChip = () => {
    const value = bccInputValue.trim();
    if (value === "") return;
    setBccChips([...bccChips, value]);
    setBccInputValue("");
  };

  const removeBccChip = (index) => {
    setBccChips(bccChips.filter((_, i) => i !== index));
  };

  const handleBccKeyDown = (e) => {
    if (e.key === "Enter" || e.key === "," || e.key === " ") {
      e.preventDefault();
      addBccChip();
    }
  };

  const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const handleAttachmentChange = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (files.length === 0) return;

    setAttachmentError("");
    if (attachments.length + files.length > MAX_ATTACHMENTS) {
      setAttachmentError(`Maximum of ${MAX_ATTACHMENTS} attachments are allowed.`);
      return;
    }

    try {
      const encoded = await Promise.all(
        files.map(async (file) => ({
          file_name: file.name,
          file: await fileToBase64(file),
        })),
      );
      setAttachments((prev) => [...prev, ...encoded]);
    } catch (err) {
      console.error("Failed to read attachment:", err);
      setAttachmentError("Failed to read one or more files.");
    }
  };

  const removeAttachment = (index) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const [errorMsg, setErrorMsg] = useState({
    to_email: "",
  });

  const sendMail = async () => {
    if (sending) return;
    const descriptionHtml = descriptionValue.trim()
      ? `<p>${descriptionValue.trim().replace(/\n/g, "<br/>")}</p>`
      : "";
    const bodyData = {
      to_email: toValue,
      cc_emails: chips,
      bcc_emails: bccChips,
      subject: subjectValue,
      body: descriptionHtml + contentValue,
      attachments,
    };
    setSending(true);
    try {
      const response = await sendQuotationMailApi(id, bodyData);
      if (response.status === 400) {
        const validationTemp = {
          to_email: "",
          cc_emails: "",
          subject: "",
          body: "",
        };
        Object.entries(response.data.error).forEach(([key, value]) => {
          if (key in validationTemp) {
            validationTemp[key] =
              Array.isArray(value) && value.length > 0 ? value[0] : "";
          }
        });
        setErrorMsg(validationTemp);
      }
      succesError(response.data);
    } catch (err) {
      console.error(err);
    } finally {
      setSending(false);
    }
  };
  const getTempalte = async () => {
    setTemplateLoading(true);
    try {
      const response = await getMailTemplateApi(id, templateId);
      if (response.success === true) {
        setContentValue(response.data.body ?? "");
        setContentViewValue(response.data.body ?? "");
        setSubjectValue(response.data.subject ?? "");
        const toEmail = response.data.contact_person_email ?? "";
        setToValue(toEmail);
        const emails = [
          response.data.sales_person_email,
          response.data.customer_email,
        ].filter(
          (email, index, arr) =>
            email &&
            email.trim() !== "" &&
            email.trim().toLowerCase() !== toEmail.trim().toLowerCase() &&
            arr.indexOf(email) === index,
        );

        setChips(emails); // will be [] if empty or all duplicate to's/each other

        setFromValue(response.data.from_email ?? "");

        // Attach the selected template's PDF (rendered server-side for this
        // quotation) automatically, without requiring "View PDF" to have
        // been clicked first.
        if (response.data.pdf_url) {
          await attachPdfFromUrl(response.data.quotation_id,response.data.pdf_url);
        }
      }
    } catch (err) {
      console.error(err);
    } finally {
      setTemplateLoading(false);
    }
  };
  // Every time the popup opens, clear it back to a blank slate first (so no
  // leftover edits from a previous open — manual CC/BCC/attachment changes,
  // typed description, etc. — can ever show through), then fetch fresh data.
  // Keyed on `show` (not just id/templateId) so reopening for the exact same
  // quotation/template also resets.
  useEffect(() => {
    if (!show) return;
    setContentValue("");
    setContentViewValue("");
    setSubjectValue("");
    setToValue("");
    setFromValue("");
    setChips([]);
    setEditValue(false);
    setDescriptionValue("");
    setInputValue("");
    setBccChips([]);
    setBccInputValue("");
    setShowBcc(false);
    setAttachments([]);
    setAttachmentError("");
    setErrorMsg({ to_email: "" });
    if (id) {
      getTempalte();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, id, templateId]);

  // Keep CC/BCC clean of whatever is currently entered as To — the same
  // address (e.g. contact person == customer email) shouldn't be sent as
  // both recipient and CC/BCC, whether it got there from the template
  // prefill or from manual typing/editing of To afterward.
  useEffect(() => {
    const to = toValue.trim().toLowerCase();
    if (!to) return;
    setChips((prev) => prev.filter((e) => e.trim().toLowerCase() !== to));
    setBccChips((prev) => prev.filter((e) => e.trim().toLowerCase() !== to));
  }, [toValue]);

  // Fetches a PDF from its S3 URL and adds it to the attachments list
  // (base64-encoded, same shape as manually picked files).
  const attachPdfFromUrl = async (quotationId,pdfUrl) => {
    try {
      const response = await fetch(pdfUrl, { method: "GET", headers: { Accept: "*/*" } });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const blob = await response.blob();
      const file = new File([blob], `${quotationId}.pdf`, {
        type: blob.type || "application/pdf",
      });
      const base64 = await fileToBase64(file);
      setAttachments((prev) =>
        prev.some((a) => a.file_name === file.name) ? prev : [...prev, { file_name: file.name, file: base64 }],
      );
    } catch (err) {
      console.error("Failed to attach template PDF:", err);
    }
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      dialogClassName="compose-modal"
    >
      <div className="compose-box">
        {/* Header */}
        <div className="compose-header d-flex justify-content-between align-items-center">
          <span className="fs-13 fw-500">New Message</span>
          <div>
            <button onClick={handleClose} className="icon-btn">
              ✕
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="compose-content">
          {templateLoading ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: "12px",
                padding: "60px 0",
              }}
            >
              <Loader height="150px" />
              <span style={{ fontSize: "13px", color: "#5f6368" }}>Loading mail content…</span>
            </div>
          ) : (
            <>
          {/* <div className="compose-row">
                        <label className="compose-label">From</label>

                        <input
                            type="text"
                            value={fromValue}
                            placeholder="Enter here"
                            className="compose-input fs-13 fw-600 textcolor flex-grow-1"
                            disabled
                        />

                    </div> */}
          <div className="compose-row">
            <label className="compose-label">To</label>

            <input
              type="text"
              value={toValue}
              onChange={(e) => setToValue(e.target.value)}
              placeholder="Enter here"
              className="compose-input fs-13 fw-600 textcolor flex-grow-1 no-focus-border"
            />
          </div>
          <span
            style={{
              color: "#ff4444",
              fontSize: "12px",
              fontWeight: "400",
            }}
          >
            {errorMsg.to_email}
          </span>
          <div className="compose-row">
            <label className="compose-label">CC</label>

            <div className="chip-input-wrapper flex-grow-1">
              {chips.map((chip, index) => (
                <span key={index} className="chip">
                  {chip}
                  <span
                    className="chip-close"
                    onClick={() => removeChip(index)}
                  >
                    ×
                  </span>
                </span>
              ))}

              <input
                type="text"
                className="chip-real-input fs-13 fw-600 textcolor no-focus-border"
                placeholder={chips.length === 0 ? "Enter here" : ""}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
              />
            </div>
            {!showBcc && (
              <span
                onClick={() => setShowBcc(true)}
                style={{ cursor: "pointer", fontSize: "12px", color: "#6B46FE", fontWeight: 600, whiteSpace: "nowrap" }}
              >
                Bcc
              </span>
            )}
          </div>
          {showBcc && (
            <div className="compose-row">
              <label className="compose-label">BCC</label>

              <div className="chip-input-wrapper flex-grow-1">
                {bccChips.map((chip, index) => (
                  <span key={index} className="chip">
                    {chip}
                    <span
                      className="chip-close"
                      onClick={() => removeBccChip(index)}
                    >
                      ×
                    </span>
                  </span>
                ))}

                <input
                  type="text"
                  className="chip-real-input fs-13 fw-600 textcolor no-focus-border"
                  placeholder={bccChips.length === 0 ? "Enter here" : ""}
                  value={bccInputValue}
                  onChange={(e) => setBccInputValue(e.target.value)}
                  onKeyDown={handleBccKeyDown}
                />
              </div>
            </div>
          )}
          <div className="compose-row">
            <label className="compose-label">Subject</label>

            <input
              type="text"
              value={subjectValue}
              onChange={(e) => setSubjectValue(e.target.value)}
              placeholder="Enter here"
              className="compose-input fs-13 fw-600 textcolor flex-grow-1 no-focus-border"
            />
          </div>
          <div className="compose-row" style={{ alignItems: "flex-start" }}>
            <label className="compose-label" style={{ paddingTop: "8px" }}>
              Description
            </label>

            <textarea
              value={descriptionValue}
              onChange={(e) => setDescriptionValue(e.target.value)}
              placeholder="Add a note (optional)"
              className="compose-input fs-13 fw-600 textcolor flex-grow-1 no-focus-border"
              rows={2}
              style={{ resize: "vertical", minHeight: "36px" }}
            />
          </div>
          {!templateId && (
            <div
              style={{
                display: "flex",
                alignItems: "end",
                justifyContent: "end",
                gap: "8px",
              }}
            >
              {/* Edit Icon — hidden when a PDF template is selected, since
                  its content shouldn't be edited in the mail popup. */}
              <div
                onClick={() => {
                  setEditValue(!editValue);
                }}
                style={{
                  cursor: "pointer",
                  width: "36px",
                  height: "36px",
                  borderRadius: "50%",

                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "all 0.2s",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.backgroundColor = "#e4e6eb")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.backgroundColor = "#f0f2f5")
                }
              >
                {!editValue && (
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#666"
                    strokeWidth="2"
                  >
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                    <path d="M18.5 2.5l3 3L12 15l-4 1 1-4 9.5-9.5z" />
                  </svg>
                )}
                {editValue && (
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#666"
                    strokeWidth="2"
                  >
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                    <path d="M18.5 2.5l3 3L12 15l-4 1 1-4 9.5-9.5z" />
                  </svg>
                )}
              </div>
            </div>
          )}
          {!(editValue && !templateId) && (
            <div
              style={{
                minHeight: "200px",
                maxHeight: "260px",
                width: "100%", // full width of parent
                maxWidth: "1000px", // optional: limit max width
                margin: "0 auto", // center if narrower than parent
                overflow: "auto",
                border: "1px solid #e0e0e0",
                borderRadius: "12px",
                backgroundColor: "#ffffff",
                boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                padding: "16px",
              }}
            >
              <div style={{ minWidth: "1200px" }}>
                {" "}
                {/* forces horizontal scroll */}
                <HtmlContent html={contentValue ?? ""} />
              </div>
            </div>
          )}

          {editValue && !templateId && (
            <ReactQuill
              theme="snow"
              value={contentValue}
              onChange={setContentValue}
              style={{
                width: "100%",
                minHeight: "200px",
                maxHeight: "200px",
                overflowY: "auto",
              }}
            />
          )}

          {attachmentError && (
            <div style={{ color: "#ff4444", fontSize: "12px", marginTop: "8px" }}>
              {attachmentError}
            </div>
          )}
          {attachments.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginTop: "10px" }}>
              {attachments.map((att, index) => (
                <div
                  key={index}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    backgroundColor: "#f1f3f4",
                    padding: "4px 10px",
                    borderRadius: "4px",
                    fontSize: "12px",
                    border: "1px solid #dadce0",
                  }}
                >
                  <span style={{ maxWidth: "180px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {att.file_name}
                  </span>
                  <span style={{ cursor: "pointer", fontWeight: "bold", padding: "0 2px" }} onClick={() => removeAttachment(index)}>
                    ×
                  </span>
                </div>
              ))}
            </div>
          )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="compose-footer d-flex justify-content-between align-items-center">
          {/* LEFT SIDE */}
          <div className="d-flex align-items-center gap-2">
            <span
              onClick={() => attachmentInputRef.current?.click()}
              title="Attach files"
              style={{ cursor: "pointer", fontSize: "13px", color: "#5f6368", fontWeight: 600 }}
            >
              📎 Attach
            </span>
            <input
              type="file"
              ref={attachmentInputRef}
              style={{ display: "none" }}
              multiple
              onChange={handleAttachmentChange}
            />
          </div>

          {/* RIGHT SIDE BUTTON */}
          <Button
            onClick={sendMail}
            className="send-btn"
            disabled={sending || templateLoading}
            style={{ display: "flex", alignItems: "center", gap: "8px" }}
          >
            {sending && <Spinner animation="border" size="sm" role="status" aria-hidden="true" />}
            {sending ? "Sending..." : "Send"}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ComposeModal;
